import React from 'react';
import { Link } from 'react-router-dom';
import { DocPage, Note, Steps, WqTable } from '../components/documentation/web-integration';

const CONTENTS = ['Introduction', 'Create your account', 'Ways to get paid', 'Payment links', 'Your website', 'Your mobile app', 'Need help?'];

const ACCOUNT_STEPS = [
    <>
        <strong>Register</strong> with your business details —{' '}
        <Link to="/registration-guide" className="wq-link">
            see the Registration Guide
        </Link>
        .
    </>,
    <>
        <strong>Explore in Test mode.</strong> Every new account starts in Test mode, so you can try payment links,
        invoices and integrations without moving real money.
    </>,
    <>
        <strong>Complete your KYC</strong> from your dashboard.
    </>,
    <>
        <strong>Go live.</strong> Once your KYC is approved, switch to Live mode and start collecting real payments.
        They are settled to the bank account you add under Settings › WayaQuick Settings › Settlement Account.
    </>,
];

const WAYS_TO_GET_PAID = [
    [
        <strong>Payment links</strong>,
        'Share a link by WhatsApp, SMS or email. One-time or subscription.',
        'No',
        <Link to="/payment-link" className="wq-link">
            Payment Links
        </Link>,
    ],
    [
        <strong>Invoices</strong>,
        'Send an itemised bill your customer can pay online.',
        'No',
        'Your dashboard',
    ],
    [
        <strong>Your website</strong>,
        'Take payments at checkout — WooCommerce plugin, SDKs or the API.',
        'Plugin: no. SDK/API: yes',
        <Link to="/sdks" className="wq-link">
            SDKs &amp; Plugins
        </Link>,
    ],
    [
        <strong>Your mobile app</strong>,
        'Take payments inside your iOS, Android or Flutter app.',
        'Yes',
        <Link to="/sdks?type=mobile" className="wq-link">
            Mobile SDKs
        </Link>,
    ],
];

function Docs() {
    return (
        <DocPage
            seo={{
                title: 'Get Started',
                description:
                    'Get started with WayaQuick: create an account, test for free, go live and choose how to accept payments — payment links, invoices, your website or your mobile app.',
            }}
            eyebrow="Merchants · Start here"
            title={
                <>
                    WayaQuick — <em>Get Started</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Get Started · Start in Test mode, then go live once your KYC is approved."
            prev={null}
            next={{ to: '/registration-guide', label: 'Registration Guide' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Introduction</h2>
                <p className="wq-lead">
                    WayaQuick helps your business collect payments from customers and pay out to bank accounts across
                    Nigeria. Customers pay on a secure WayaQuick checkout by dynamic virtual account, bank transfer,
                    card, USSD or PayAttitude, and you track everything from one dashboard.
                </p>
                <p className="wq-p">
                    You don't need a website or a developer to start — a payment link is enough. When you're ready,
                    you can build WayaQuick into your website or app.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Create your account</h2>
                <Steps items={ACCOUNT_STEPS} />
                <Note label="Test first, then go live">
                    Test mode is free and has no approval step, so you can set everything up while your KYC is being
                    reviewed.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">Ways to get paid</h2>
                <p className="wq-p">Use whichever fits how you sell — you can use more than one.</p>
                <WqTable columns={['Option', 'What it does', 'Developer needed?', 'Learn more']} rows={WAYS_TO_GET_PAID} />
                <Note label="Exact amounts only">
                    Whichever option you use, customers pay the exact amount requested. A payment for more or less is
                    rejected automatically, and any money that came through is reversed to the customer.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[3]}>
                <h2 className="wq-h2">Payment links</h2>
                <p className="wq-p">
                    Payment links let you collect money without building anything. Create one from{' '}
                    <strong>Payment Links</strong> in your dashboard's side menu and share it anywhere.
                </p>
                <ul className="wq-p wq-list">
                    <li>
                        <strong>One-time payment link</strong> — charges the customer once. Good for product sales,
                        tickets and donations.
                    </li>
                    <li>
                        <strong>Subscription payment link</strong> — charges the customer on a schedule, such as
                        weekly or monthly, for as many times as you choose.
                    </li>
                </ul>
                <p className="wq-p">
                    Every link has a fixed amount that you set when you create it. Customers can't change the amount
                    on the checkout page.
                </p>
                <p className="wq-p">
                    <Link to="/payment-link" className="wq-link">
                        Step-by-step guide to payment links
                    </Link>
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[4]}>
                <h2 className="wq-h2">Your website</h2>
                <ul className="wq-p wq-list">
                    <li>
                        <strong>WooCommerce plugin</strong> — for WordPress stores. Install, add your keys, and
                        WayaQuick appears at checkout. No code.
                    </li>
                    <li>
                        <strong>Server SDKs</strong> — PHP, .NET, Java, Node.js and Go libraries for your backend.
                    </li>
                    <li>
                        <strong>Standard API</strong> — call the WayaQuick Merchant API directly from any language.
                    </li>
                </ul>
                <p className="wq-p">
                    Start with the{' '}
                    <Link to="/sdks" className="wq-link">
                        SDKs &amp; Plugins
                    </Link>{' '}
                    guide, or the{' '}
                    <Link to="/standard-api-integration" className="wq-link">
                        Standard API Integration
                    </Link>{' '}
                    if you're calling the API yourself.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[5]}>
                <h2 className="wq-h2">Your mobile app</h2>
                <p className="wq-p">
                    Take payments inside your app with the WayaQuick SDKs for iOS, Android and Flutter. See the{' '}
                    <Link to="/sdks?type=mobile" className="wq-link">
                        Mobile SDKs
                    </Link>{' '}
                    guide.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[6]}>
                <h2 className="wq-h2">Need help?</h2>
                <p className="wq-p">
                    If you get stuck setting up or integrating WayaQuick, or your app uses a technology we don't have
                    an SDK for, our team can help. Reach us through the{' '}
                    <Link to="/contact" className="wq-link">
                        contact page
                    </Link>{' '}
                    or email{' '}
                    <a href="mailto:it@wayaquick.com" className="wq-link">
                        it@wayaquick.com
                    </a>
                    .
                </p>
            </section>
        </DocPage>
    );
}

export default Docs;

